import React, { useEffect, useMemo, useState } from 'react';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import { editorTopicsAPI, TopicsInfo } from 'api/editor/topics';
import { Stack } from '@mui/material';
import GlossaryPopupLeftPanel from './GlossaryPopupLeftPanel';
import GlossaryPopupBody from './GlossaryPopupBody';
import useBundleTranslation from 'i18n';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { createPortal } from 'react-dom';
import { useGlossaryTermVisibility } from 'hooks/useGlossaryTerm';

export default function GlossaryTermPopup({
    showPopup,
    setShowPopup,
    topicIds,
    topicId,
    showViewRelatedButton = true,
    viewRelatedButtonLabel,
    enableViewRelatedEvent = false,
    popupTitle,
    filterTermsByStatus = true,
}: {
    showPopup: boolean;
    setShowPopup: (show: boolean) => void;
    topicIds: number[];
    topicId: number;
    showViewRelatedButton?: boolean;
    viewRelatedButtonLabel?: string;
    enableViewRelatedEvent?: boolean;
    popupTitle?: string;
    filterTermsByStatus?: boolean;
}) {
    const { t } = useBundleTranslation('components/glossary-term/glossary');

    const [topicsInfo, setTopicsInfo] = useState<TopicsInfo>({});
    const [readyToShow, setReadyToShow] = useState(false);
    const [selectedTopic, setSelectedTopic] = useState<number>(topicId);
    const { isVisibleGlossaryTermByStatus } = useGlossaryTermVisibility();

    useEffect(() => {
        if (showPopup && topicIds.length > 0) {
            editorTopicsAPI.getTopicsInfo(topicIds).then((response) => {
                if (response.status === 'OK' && response.data) {
                    const rawData = response.data;
                    delete rawData['default-topic-type'];

                    if (filterTermsByStatus) {
                        Object.keys(rawData ?? {}).forEach((sectionKey: any) => {
                            rawData[sectionKey].topics = rawData[sectionKey].topics.filter((topic: any) => {
                                return isVisibleGlossaryTermByStatus(topic);
                            });
                        });
                        Object.keys(rawData ?? {}).forEach((sectionKey: any) => {
                            if (rawData[sectionKey].topics.length == 0) delete rawData[sectionKey];
                        });
                    }
                    setTopicsInfo(rawData);
                    setReadyToShow(true);
                }
            });
        } else {
            setReadyToShow(false);
        }
    }, [showPopup, topicIds]);

    const popupSettings: PopupSettings = {
        noButtons: true,
        title: popupTitle ?? t('popup_title'),
    };

    const glossaryTerms = useMemo(() => Object.keys(topicsInfo), [topicsInfo]);

    return readyToShow && glossaryTerms.length > 0 ? (
        <Popup
            settings={popupSettings}
            open={readyToShow}
            onHide={() => {
                setShowPopup(false);
                setReadyToShow(false);
            }}
            scrollType={'body'}
            maxWidth={'popupXl'}
            sx={{ '.MuiPaper-root': { width: '1176px !important', maxWidth: 'none !important' } }}
        >
            <Stack direction={'row'} key={'glossary-terms-container'} sx={{ margin: '-16px -24px' }}>
                <GlossaryPopupLeftPanel
                    setSelectedTopic={setSelectedTopic}
                    selectedTopic={selectedTopic}
                    topicsInfo={topicsInfo}
                />
                <GlossaryPopupBody
                    selectedTopic={selectedTopic}
                    setSelectedTopic={setSelectedTopic}
                    topicsInfo={topicsInfo}
                    showViewRelatedButton={showViewRelatedButton}
                    viewRelatedButtonLabel={viewRelatedButtonLabel}
                    enableViewRelatedEvent={enableViewRelatedEvent}
                />
            </Stack>
        </Popup>
    ) : (
        createPortal(<LoadingPlaceholder variant={'secondary'} relativeToParent sx={{ zIndex: 2000 }} />, document.body)
    );
}
